import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Grid,
  useTheme,
  Button,
} from '@mui/material';
import {
  Description,
  Download,
  CheckCircle,
  Warning,
  Search,
  FilterList,
} from '@mui/icons-material';

const DocumentCard = ({ doc, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();

  const getStatusConfig = (status) => {
    switch (status) {
      case 'validated':
        return {
          icon: <CheckCircle fontSize="small" />,
          label: 'Validé',
          color: theme.palette.success.main,
          bgColor: theme.palette.success.light,
        };
      case 'pending':
        return {
          icon: <Warning fontSize="small" />,
          label: 'En attente',
          color: theme.palette.warning.main,
          bgColor: theme.palette.warning.light,
        };
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig(doc.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Paper
        elevation={isHovered ? 8 : 1}
        sx={{
          p: 3,
          height: '100%',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Description
            sx={{
              fontSize: 40,
              color: theme.palette.primary.main,
              mr: 2,
            }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom>
              {doc.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ajouté le {doc.date} • {doc.type}
            </Typography>
          </Box>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            sx={{
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              '& .MuiChip-icon': {
                color: 'inherit',
              },
            }}
          />
        </Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Télécharger
            </Button>
          </Box>
        </motion.div>
      </Paper>
    </motion.div>
  );
};

const Documents = () => {
  const theme = useTheme();
  const [searchFocused, setSearchFocused] = useState(false);

  const documents = [
    {
      name: 'Statuts de l\'entreprise',
      type: 'PDF',
      status: 'validated',
      date: '15/03/2024',
    },
    {
      name: 'Kbis',
      type: 'PDF',
      status: 'pending',
      date: '15/03/2024',
    },
    {
      name: 'Attestation URSSAF',
      type: 'PDF',
      status: 'validated',
      date: '14/03/2024',
    },
    {
      name: 'Mandat de gestion',
      type: 'PDF',
      status: 'pending',
      date: '14/03/2024',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.dark} 100%)`,
        p: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h3"
          sx={{
            mb: 4,
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          Mes Documents
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 4,
          }}
        >
          <Paper
            elevation={searchFocused ? 8 : 1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              flex: 1,
              maxWidth: 400,
              transition: 'all 0.3s ease',
            }}
          >
            <Search sx={{ color: 'text.secondary', mr: 1 }} />
            <input
              placeholder="Rechercher un document..."
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                fontSize: '1rem',
                background: 'transparent',
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
          </Paper>
          <IconButton
            sx={{
              backgroundColor: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
            }}
          >
            <FilterList />
          </IconButton>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        <AnimatePresence>
          {documents.map((doc, index) => (
            <Grid item xs={12} md={6} key={doc.name}>
              <DocumentCard doc={doc} index={index} />
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Box>
  );
};

export default Documents; 